import React, { useCallback, useSyncExternalStore } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/button';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Text } from '@atlaskit/primitives/compiled/text';

import { PanelName } from '@confluence/object-sidebar-api/entry-points/PanelName.ts';
import {
	useIsObjectSidebarShown,
	useObjectSidebarActions,
	usePanel,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';

import type { SuggestionItem } from '../SuggestionCard.tsx';

import { SuggestionReviewPanel } from './SuggestionReviewPanel.tsx';

export const SPACE_SUGGESTION_REVIEW_PANEL_ID: PanelName.SpaceSuggestionReviewPanel =
	PanelName.SpaceSuggestionReviewPanel;
export const SPACE_SUGGESTION_REVIEW_PANEL_WIDTH = 400;

const i18n = defineMessages({
	title: {
		id: 'space-suggestions.review-panel.title',
		defaultMessage: 'Review suggestion',
		description: 'Title for the suggestion review panel',
	},
	cancel: {
		id: 'space-suggestions.review-panel.cancel-button',
		defaultMessage: 'Cancel',
		description: 'Label for the cancel button on the suggestion review panel',
	},
	approve: {
		id: 'space-suggestions.review-panel.approve-button',
		defaultMessage: 'Approve',
		description: 'Label for the approve button on the suggestion review panel',
	},
	subtext: {
		id: 'space-suggestions.review-panel.subtext',
		defaultMessage: '{count, plural, one {# suggested change} other {# suggested changes}}',
		description:
			'Subtext shown below the title in the suggestion review panel indicating the number of suggested changes',
	},
});

type PanelState = {
	item: SuggestionItem;
	onApprove: (acceptedActionStepIds: string[], declinedActionStepIds: string[]) => void;
} | null;

let panelState: PanelState = null;
const listeners = new Set<() => void>();

const emitChange = () => {
	listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

const getSnapshot = () => panelState;

export const useSpaceSuggestionReviewPanelItem = (): PanelState =>
	useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

export const setSpaceSuggestionReviewPanelItem = (
	item: SuggestionItem,
	onApprove: (acceptedActionStepIds: string[], declinedActionStepIds: string[]) => void,
): void => {
	panelState = { item, onApprove };
	emitChange();
};

export const clearSpaceSuggestionReviewPanelItem = (): void => {
	if (panelState === null) {
		return;
	}

	panelState = null;
	emitChange();
};

export const SpaceSuggestionReviewPanelHeaderAfterIcon = (): React.JSX.Element | null => {
	const state = useSpaceSuggestionReviewPanelItem();
	const { formatMessage } = useIntl();

	if (state === null) {
		return null;
	}

	return (
		<Text size="small" color="color.text.subtlest">
			{formatMessage(i18n.subtext, { count: state.item.rawSuggestion.actions.length })} •{' '}
			{state.item.timestamp}
		</Text>
	);
};

export const SpaceSuggestionReviewPanelBody = (): React.JSX.Element | null => {
	const state = useSpaceSuggestionReviewPanelItem();
	const { hideObjectSidebarByPanelId } = useObjectSidebarActions();

	const handleClose = useCallback(() => {
		clearSpaceSuggestionReviewPanelItem();
		hideObjectSidebarByPanelId(SPACE_SUGGESTION_REVIEW_PANEL_ID);
	}, [hideObjectSidebarByPanelId]);

	const handleApprove = useCallback(
		(acceptedActionStepIds: string[], declinedActionStepIds: string[]) => {
			state?.onApprove(acceptedActionStepIds, declinedActionStepIds);
			handleClose();
		},
		[state, handleClose],
	);

	if (state === null) {
		return null;
	}

	return (
		<SuggestionReviewPanel
			rawSuggestion={state.item.rawSuggestion}
			onClose={handleClose}
			onCancel={handleClose}
			onApprove={handleApprove}
			isEmbedded
		/>
	);
};

export const SpaceSuggestionReviewPanelFooter = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { hideObjectSidebarByPanelId } = useObjectSidebarActions();

	const handleClose = useCallback(() => {
		clearSpaceSuggestionReviewPanelItem();
		hideObjectSidebarByPanelId(SPACE_SUGGESTION_REVIEW_PANEL_ID);
	}, [hideObjectSidebarByPanelId]);

	return (
		<Flex direction="row" gap="space.100" justifyContent="end">
			<Button onClick={handleClose}>{formatMessage(i18n.cancel)}</Button>
			<Button appearance="primary" onClick={handleClose}>
				{formatMessage(i18n.approve)}
			</Button>
		</Flex>
	);
};

export const useShowSpaceSuggestionReviewPanel = (): {
	isSpaceSuggestionReviewPanelShown: boolean;
	showSpaceSuggestionReviewPanel: (
		item: SuggestionItem,
		onApprove: (acceptedActionStepIds: string[], declinedActionStepIds: string[]) => void,
	) => void;
} => {
	const { showObjectSidebar } = useObjectSidebarActions();
	const isObjectSidebarShown = useIsObjectSidebarShown();
	const panel = usePanel();

	const showSpaceSuggestionReviewPanel = useCallback(
		(
			item: SuggestionItem,
			onApprove: (acceptedActionStepIds: string[], declinedActionStepIds: string[]) => void,
		) => {
			setSpaceSuggestionReviewPanelItem(item, onApprove);
			showObjectSidebar(
				{
					id: SPACE_SUGGESTION_REVIEW_PANEL_ID,
					headerComponentElements: {
						headerLabel: i18n.title,
						HeaderAfterIconElement: SpaceSuggestionReviewPanelHeaderAfterIcon,
					},
					BodyComponent: SpaceSuggestionReviewPanelBody,
					FooterComponent: SpaceSuggestionReviewPanelFooter,
					closeOptions: {
						onPanelClose: clearSpaceSuggestionReviewPanelItem,
					},
				},
				'push',
			);
		},
		[showObjectSidebar],
	);

	return {
		isSpaceSuggestionReviewPanelShown:
			isObjectSidebarShown && panel?.id === SPACE_SUGGESTION_REVIEW_PANEL_ID,
		showSpaceSuggestionReviewPanel,
	};
};

type SpaceSuggestionReviewSidebarPanelProps = {
	onClosePanel: () => void;
	onApprove: (acceptedActionStepIds: string[], declinedActionStepIds: string[]) => void;
};

export function SpaceSuggestionReviewSidebarPanel({
	onClosePanel,
	onApprove,
}: SpaceSuggestionReviewSidebarPanelProps): React.JSX.Element | null {
	const state = useSpaceSuggestionReviewPanelItem();

	const handleClose = useCallback(() => {
		clearSpaceSuggestionReviewPanelItem();
		onClosePanel();
	}, [onClosePanel]);

	const handleApprove = useCallback(
		(acceptedActionStepIds: string[], declinedActionStepIds: string[]) => {
			onApprove(acceptedActionStepIds, declinedActionStepIds);
			handleClose();
		},
		[onApprove, handleClose],
	);

	if (state === null) {
		return null;
	}

	return (
		<SuggestionReviewPanel
			rawSuggestion={state.item.rawSuggestion}
			onClose={handleClose}
			onCancel={handleClose}
			onApprove={handleApprove}
			isEmbedded
		/>
	);
}
